<script setup lang="ts">
import {
  Lock as LockIcon,
  Copy as CopyIcon,
  Globe as GlobeIcon,
  Users as UsersIcon,
  Mail as MailIcon,
  UserPlus as UserPlusIcon,
  Trash2 as TrashIcon,
  AlertTriangle as AlertTriangleIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  X as XIcon,
  Share2 as Share2Icon,
  Download as DownloadIcon,
  Link as LinkIcon,
  RefreshCw as RefreshCwIcon,
} from '@lucide/vue'
import maplibregl from 'maplibre-gl'
import { useField } from 'vee-validate'
import { computed, nextTick, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import BaseModal from '@/components/BaseModal.vue'
import BaseBadge from '@/components/ui/BaseBadge.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import BaseEmptyState from '@/components/ui/BaseEmptyState.vue'
import BaseIconButton from '@/components/ui/BaseIconButton.vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import BaseSearchBar from '@/components/ui/BaseSearchBar.vue'
import BaseSegmentedControl from '@/components/ui/BaseSegmentedControl.vue'
import BaseSelect from '@/components/ui/BaseSelect.vue'
import BaseSpinner from '@/components/ui/BaseSpinner.vue'
import BaseToggle from '@/components/ui/BaseToggle.vue'
import { useIOSKeyboard } from '@/composables/useIOSKeyboard'
import { loadRoutecampMapStyle } from '@/config/mapStyle'
import { apiService } from '@/services/apiService'
import { showConfirm } from '@/services/dialogService'
import { downloadFile } from '@/services/downloadService'
import { formatTime } from '@/services/formattingService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { requireOnlineModificationAccess } from '@/services/offlineModificationGuardService'
import { requirePermission } from '@/services/permissionService'
import { isTauri, isTauriAndroidMobile, webOrigin } from '@/services/platformService'
import { applyRoutecampContours } from '@/services/routecampContours'
import { toNativeTileUrl } from '@/services/routecampTilesUrl'
import { getTrackCoordinates, formatKm } from '@/services/tourStatsService'
import { shouldHideAscentForTrack } from '@/services/tourTypeService'
import { TrackColorService } from '@/services/trackColorService'
import { getTrackElevationGain } from '@/services/trackMetricsCalculationService'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { InvitedUser } from '@/types/user'
import {
  AssignableWorkspaceRoles,
  WorkspaceRole,
  WorkspaceRoleDisplayNames,
  type WorkspaceMember,
  type WorkspaceInvitation,
  type WorkspaceRoleType,
} from '@/types/workspace'
import { emailValidationRules } from '@/utils/validation'

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

const workspaceStore = useWorkspaceStore()
const router = useRouter()
const { getInputHandlers } = useIOSKeyboard()

// State
const isPublic = ref(false)
const activeTab = ref<'share' | 'people'>('share')

// Member management state
const members = ref<WorkspaceMember[]>([])
const invitations = ref<WorkspaceInvitation[]>([])
const isLoadingMembers = ref(false)
const isLoadingInvitations = ref(false)
const isInviting = ref(false)

// Email autocomplete state
const invitedUsers = ref<InvitedUser[]>([])
const isLoadingInvitedUsers = ref(false)
const emailInputFocused = ref(false)
const highlightedIndex = ref(-1)
const suggestionListId = 'share-invite-email-suggestions'
const emailReadonly = ref(true)

// Message state
type MessageType = 'success' | 'error' | 'info'
interface Message {
  id: string
  type: MessageType
  text: string
  timestamp: number
}

const messages = ref<Message[]>([])

const addMessage = (type: MessageType, text: string) => {
  const message: Message = {
    id: Math.random().toString(36).substring(2, 11),
    type,
    text,
    timestamp: Date.now(),
  }
  messages.value.push(message)

  // Auto-remove after 5 seconds
  setTimeout(() => {
    removeMessage(message.id)
  }, 5000)
}

const removeMessage = (id: string) => {
  const index = messages.value.findIndex((msg) => msg.id === id)
  if (index > -1) {
    messages.value.splice(index, 1)
  }
}

const showMessage = {
  success: (text: string) => addMessage('success', text),
  error: (text: string) => addMessage('error', text),
  info: (text: string) => addMessage('info', text),
}

// Setup validation for invite member form
const {
  value: newMemberEmail,
  errorMessage: memberEmailError,
  resetField: resetMemberEmail,
  validate: validateMemberEmail,
} = useField('Email', emailValidationRules, { validateOnValueUpdate: false })

const newMemberRole = ref<WorkspaceRoleType>(WorkspaceRole.VIEWER)
const hasTriedInvite = ref(false)

// Computed property for type-safe v-model binding
const memberEmailValue = computed({
  get: () => (newMemberEmail.value as string) || '',
  set: (value: string) => (newMemberEmail.value = value),
})

const memberSearchQuery = ref('')
const invitationSearchQuery = ref('')

const currentWorkspace = ref(workspaceStore.currentWorkspace)

// A collection can only be shared once it exists on the server. `_createEventPushed` alone is
// not enough — it is true while the WORKSPACE_CREATED is merely queued, and the member/invite/
// visibility API calls would 404. See docs/concepts/sync-ordering-edge-cases.md (Group 2).
const canShare = computed(() => workspaceStore.isPersistedToServer)

const shareBlockedReason = computed(() => {
  if (workspaceStore.isPersistedToServer) return null
  if (!workspaceStore._createEventPushed) {
    return 'Add some content to your collection (tracks, waypoints, or photos) before sharing.'
  }
  return 'This collection is still syncing. Sharing will be available once it finishes.'
})

const canManageSharing = computed(() => {
  return workspaceStore.canManageWorkspace
})

const workspaceUrl = computed(() => {
  if (!currentWorkspace.value?.clientId || !canShare.value) return ''

  const route = router.resolve({
    name: 'workspace-detail',
    params: { workspaceId: currentWorkspace.value.clientId },
  })

  return `${webOrigin()}${route.href}`
})

const assignableRoles: readonly WorkspaceRoleType[] = AssignableWorkspaceRoles

const roleOptions = computed(() => [
  ...assignableRoles
    .filter((role) => role !== WorkspaceRole.OWNER)
    .map((role) => ({
      id: role,
      name: WorkspaceRoleDisplayNames[role],
    })),
])

const tabOptions = computed(() => [
  { value: 'share', label: 'Share', icon: Share2Icon },
  {
    value: 'people',
    label: 'People',
    icon: UsersIcon,
    badge: members.value.length + invitations.value.length || null,
  },
])

const setActiveTab = async (value: string | number) => {
  if (value === 'people' && !canManageSharing.value) return
  activeTab.value = value === 'people' ? 'people' : 'share'
  await nextTick()
  document
    .getElementById(`collection-share-panel-${activeTab.value}`)
    ?.closest('main')
    ?.scrollTo({ top: 0 })
}

const isOwnerMember = (member: WorkspaceMember) => {
  return member.role === WorkspaceRole.OWNER || member.roleDisplay.toLowerCase() === 'owner'
}

// Share image state (similar to TourShareModal)
const isGenerating = ref(false)
const imageDataUrl = ref<string | null>(null)
const errorText = ref<string | null>(null)
const usedFallback = ref(false)

const distanceKm = computed(() => formatKm(workspaceStore.totalDistance))
const visibleCollectionElevationGain = computed(() => {
  let totalGain: number | null = null
  for (const tour of workspaceStore.tours) {
    for (const track of tour.tracks) {
      if (shouldHideAscentForTrack(track, tour.type)) continue
      const gain = getTrackElevationGain(track)
      if (gain && gain > 0) totalGain = (totalGain ?? 0) + gain
    }
  }
  return totalGain
})
const elevationHm = computed(() =>
  visibleCollectionElevationGain.value !== null
    ? Math.round(visibleCollectionElevationGain.value).toString()
    : null,
)
const durationText = computed(() => {
  const secs = workspaceStore.totalTimeElapsed
  return secs ? formatTime(secs) : ''
})

// Filtered members and invitations
const filteredMembers = computed(() => {
  if (!memberSearchQuery.value.trim()) return members.value

  const query = memberSearchQuery.value.toLowerCase().trim()
  return members.value.filter(
    (member: WorkspaceMember) =>
      member.name.toLowerCase().includes(query) ||
      member.email.toLowerCase().includes(query) ||
      member.roleDisplay.toLowerCase().includes(query),
  )
})

const filteredInvitations = computed(() => {
  if (!invitationSearchQuery.value.trim()) return invitations.value

  const query = invitationSearchQuery.value.toLowerCase().trim()
  return invitations.value.filter(
    (invitation: WorkspaceInvitation) =>
      invitation.email.toLowerCase().includes(query) ||
      invitation.roleDisplay.toLowerCase().includes(query) ||
      invitation.invitedByName.toLowerCase().includes(query),
  )
})

// Email suggestions based on invited users
const emailQuery = computed(() => memberEmailValue.value.trim().toLowerCase())
const emailSuggestions = computed(() => {
  if (!emailQuery.value) return [] as InvitedUser[]
  const existing = new Set(members.value.map((m: WorkspaceMember) => m.email.toLowerCase()))
  const q = emailQuery.value
  return invitedUsers.value
    .filter((u: InvitedUser) => {
      const email = u.email?.toLowerCase() || ''
      const name = u.profileName?.toLowerCase() || ''
      return email.includes(q) || name.includes(q)
    })
    .filter((u: InvitedUser) => !existing.has(u.email.toLowerCase()))
    .slice(0, 8)
})
const showEmailSuggestions = computed(
  () => emailInputFocused.value && emailSuggestions.value.length > 0,
)

// Methods
const copyUrl = async () => {
  try {
    await navigator.clipboard.writeText(workspaceUrl.value)
    showMessage.success('Collection link copied')
  } catch {
    showMessage.error('Failed to copy URL to clipboard')
  }
}

const loadMembers = async () => {
  if (!currentWorkspace.value?.clientId) return
  // The collection isn't on the server yet — skip the API read (it would 404).
  if (!workspaceStore.isPersistedToServer) return

  try {
    isLoadingMembers.value = true
    members.value = await apiService.getWorkspaceMembers(currentWorkspace.value.clientId, {
      suppressToast: true,
    })
  } catch (error) {
    showMessage.error(
      `Failed to load members: ${error instanceof Error ? error.message : String(error)}`,
    )
  } finally {
    isLoadingMembers.value = false
  }
}

const loadInvitations = async () => {
  if (!currentWorkspace.value?.clientId) return
  // The collection isn't on the server yet — skip the API read (it would 404).
  if (!workspaceStore.isPersistedToServer) return

  try {
    isLoadingInvitations.value = true
    invitations.value = await apiService.getWorkspaceInvitations(currentWorkspace.value.clientId, {
      suppressToast: true,
    })
  } catch (error) {
    showMessage.error(
      `Failed to load invitations: ${error instanceof Error ? error.message : String(error)}`,
    )
  } finally {
    isLoadingInvitations.value = false
  }
}

const loadInvitedUsers = async () => {
  try {
    isLoadingInvitedUsers.value = true
    invitedUsers.value = await apiService.getInvitedUsers({ suppressToast: true })
  } catch {
    // Quietly ignore autocomplete errors to not disturb user flow
  } finally {
    isLoadingInvitedUsers.value = false
  }
}

const onEmailFocus = (e: FocusEvent) => {
  try {
    getInputHandlers().onFocus(e)
  } catch {}
  emailInputFocused.value = true
  // Toggle readonly off immediately to ensure mobile keyboard appears
  emailReadonly.value = false
}
const onEmailBlur = () => {
  try {
    getInputHandlers().onBlur()
  } catch {}
  // Delay closing to allow click on suggestions
  setTimeout(() => (emailInputFocused.value = false), 120)
  // Re-enable readonly so managers won't target it next time
  setTimeout(() => {
    emailReadonly.value = true
  }, 150)
}
const selectEmailSuggestion = (u: InvitedUser) => {
  memberEmailValue.value = u.email
  emailInputFocused.value = false
}

const onEmailKeydown = (e: KeyboardEvent) => {
  if (!showEmailSuggestions.value && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
    emailInputFocused.value = true
  }
  if (!emailSuggestions.value.length) return
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    highlightedIndex.value = (highlightedIndex.value + 1) % emailSuggestions.value.length
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    highlightedIndex.value =
      (highlightedIndex.value - 1 + emailSuggestions.value.length) % emailSuggestions.value.length
  } else if (e.key === 'Enter') {
    if (highlightedIndex.value >= 0 && highlightedIndex.value < emailSuggestions.value.length) {
      e.preventDefault()
      selectEmailSuggestion(emailSuggestions.value[highlightedIndex.value])
    }
  } else if (e.key === 'Escape') {
    emailInputFocused.value = false
  }
}

// Reset highlight when query changes
watch(
  () => memberEmailValue.value,
  () => {
    highlightedIndex.value = -1
  },
)

// Highlight matches in suggestions (email or profile name)
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function highlightAutocompleteMatch(text: string | undefined | null): string {
  const t = text ?? ''
  const q = (memberEmailValue.value || '').trim()
  if (!q) return escapeHtml(t)
  const lower = t.toLowerCase()
  const qLower = q.toLowerCase()
  let i = 0
  const chunks: string[] = []
  while (true) {
    const idx = lower.indexOf(qLower, i)
    if (idx === -1) break
    chunks.push(escapeHtml(t.slice(i, idx)))
    chunks.push(
      '<span class="font-semibold">',
      escapeHtml(t.slice(idx, idx + qLower.length)),
      '</span>',
    )
    i = idx + qLower.length
  }
  chunks.push(escapeHtml(t.slice(i)))
  return chunks.join('')
}

const updateMemberRole = async (member: WorkspaceMember, newRole: WorkspaceRoleType) => {
  if (!currentWorkspace.value?.clientId) return
  if (isOwnerMember(member) || newRole === WorkspaceRole.OWNER) return
  if (!(await requireOnlineModificationAccess('update workspace member role'))) return

  try {
    await apiService.updateMemberRole(currentWorkspace.value.clientId, member.userId, newRole, {
      suppressToast: true,
    })
    showMessage.success(`Updated ${member.name}'s role`)
    await loadMembers()
  } catch (error) {
    showMessage.error(
      `Failed to update member role: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

const removeMember = async (member: WorkspaceMember) => {
  if (!currentWorkspace.value?.clientId) return
  if (isOwnerMember(member)) {
    showMessage.error('The collection owner cannot be removed')
    return
  }
  if (!(await requireOnlineModificationAccess('remove workspace member'))) return

  const confirmed = await showConfirm({
    title: 'Remove member?',
    message: `${member.name} will lose access to this collection.`,
    confirmText: 'Remove',
    variant: 'danger',
  })
  if (!confirmed) {
    return
  }

  try {
    await apiService.removeMember(currentWorkspace.value.clientId, member.userId, {
      suppressToast: true,
    })
    showMessage.success(`Removed ${member.name} from workspace`)
    await loadMembers()
  } catch (error) {
    showMessage.error(
      `Failed to remove member: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

const inviteMember = async () => {
  if (!currentWorkspace.value?.clientId || !memberEmailValue.value.trim()) return
  if (!(await requireOnlineModificationAccess('invite workspace member'))) return

  // Validate only on Add click
  hasTriedInvite.value = true
  const result = await validateMemberEmail()
  if (!result.valid) {
    showMessage.error('Please enter a valid email address')
    return
  }

  try {
    isInviting.value = true
    const result = await apiService.inviteMemberByEmail(
      currentWorkspace.value.clientId,
      memberEmailValue.value.trim(),
      newMemberRole.value,
    )

    if (result.success) {
      if (result.invitationSent) {
        showMessage.success(`Invitation sent to ${memberEmailValue.value}`)
        await loadInvitations()
      }
      // Reset form
      resetMemberEmail()
      hasTriedInvite.value = false
      newMemberRole.value = WorkspaceRole.VIEWER
    } else {
      showMessage.error(result.message || 'Failed to invite member')
    }
  } catch (error) {
    showMessage.error(
      `Failed to invite member: ${error instanceof Error ? error.message : String(error)}`,
    )
  } finally {
    isInviting.value = false
  }
}

const cancelInvitation = async (invitation: WorkspaceInvitation) => {
  if (!currentWorkspace.value?.clientId) return
  if (!(await requireOnlineModificationAccess('cancel workspace invitation'))) return

  const confirmed = await showConfirm({
    title: 'Cancel invitation?',
    message: `${invitation.email} will no longer be able to accept this invitation.`,
    confirmText: 'Cancel invitation',
    variant: 'danger',
  })
  if (!confirmed) {
    return
  }

  try {
    await apiService.cancelInvitation(currentWorkspace.value.clientId, invitation.id, {
      suppressToast: true,
    })
    showMessage.success(`Cancelled invitation for ${invitation.email}`)
    await loadInvitations()
  } catch (error) {
    showMessage.error(
      `Failed to cancel invitation: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

const handleToggle = async () => {
  if (!canShare.value) {
    showMessage.info(shareBlockedReason.value ?? 'Please save your collection before sharing')
    return
  }

  if (!(await requirePermission('toggle_public'))) return

  isPublic.value = !isPublic.value
  workspaceStore.updateWorkspace({
    isPublic: isPublic.value,
  })
}

// Workspace share helpers (image generation similar to TourShareModal)
async function generateShareImage() {
  isGenerating.value = true
  errorText.value = null
  usedFallback.value = false
  try {
    logService.info(LOG_CATEGORIES.UI, 'Generating workspace share image: start', {
      tours: workspaceStore.tours.length,
      tracks: workspaceStore.tours.reduce((sum, t) => sum + t.tracks.length, 0),
    })
    const size = 1080
    const baseUrl = await tryCreateMapSnapshotForWorkspace(size, size)
    const composed = await composeWorkspaceImage(baseUrl, size, size)
    imageDataUrl.value = composed
    logService.success(LOG_CATEGORIES.UI, 'Workspace share image generated', {
      workspaceId: currentWorkspace.value?.clientId,
      usedFallback: usedFallback.value,
    })
  } catch (error) {
    logService.error(LOG_CATEGORIES.UI, 'Failed generating workspace share image', {
      workspaceId: currentWorkspace.value?.clientId,
      error,
    })
    errorText.value = 'Failed to generate image.'
  } finally {
    isGenerating.value = false
  }
}

async function tryCreateMapSnapshotForWorkspace(width: number, height: number): Promise<string> {
  const mapStyle = await loadRoutecampMapStyle()
  const bounds = getWorkspaceBounds()
  if (!bounds) {
    logService.warn(LOG_CATEGORIES.UI, 'No bounds computed for workspace share image', {
      tours: workspaceStore.tours.length,
      tracks: workspaceStore.tours.reduce((sum, t) => sum + t.tracks.length, 0),
    })
    throw new Error('No track coordinates to render')
  }

  const mapBounds = new maplibregl.LngLatBounds(
    [bounds.minLon, bounds.minLat],
    [bounds.maxLon, bounds.maxLat],
  )
  const container = document.createElement('div')
  container.style.position = 'fixed'
  container.style.left = '-99999px'
  container.style.top = '-99999px'
  container.style.width = `${width}px`
  container.style.height = `${height}px`
  container.style.opacity = '0'
  container.style.pointerEvents = 'none'
  document.body.appendChild(container)

  const map = new maplibregl.Map({
    container,
    style: mapStyle,
    bounds: mapBounds,
    fitBoundsOptions: { padding: 40, animate: false },
    interactive: false,
    attributionControl: false,
    fadeDuration: 0,
    pitch: 0,
    bearing: 0,
    canvasContextAttributes: { preserveDrawingBuffer: true },
    transformRequest: isTauri()
      ? (url) => {
          const nativeUrl = toNativeTileUrl(url, isTauriAndroidMobile())
          return nativeUrl ? { url: nativeUrl } : undefined
        }
      : undefined,
  })

  const cleanup = () => {
    try {
      map.remove()
    } catch {}
    container.remove()
  }

  await new Promise<void>((resolve, reject) => {
    let timeout = 0
    const onError = (event: unknown) => {
      const error = (event as { error?: unknown })?.error ?? event
      logService.warn(LOG_CATEGORIES.MAP, 'Collection share map resource failed to load', {
        error,
      })
    }
    const onLoad = () => {
      clearTimeout(timeout)
      map.off('error', onError)
      map.off('load', onLoad)
      // Keep geographic context prominent while omitting detailed POIs and address labels.
      try {
        applyRoutecampContours(map)
        const base = ['Background']
        const terrain = [
          'Hillshade',
          'Contour',
          'Contour index',
          'Contour labels',
          'Glacier',
          'Glacier contour',
          'Glacier contour index',
          'Glacier contour labels',
          'Sand',
          'Cliff',
          'Cliff line',
        ]
        const vegetation = ['Forest', 'Tree', 'Wood', 'Park', 'Park outline', 'Scrub', 'Grass']
        const water = [
          'Water',
          'River',
          'River tunnel',
          'River labels',
          'Lake labels',
          'Lakeline labels',
          'Ocean labels',
          'Sea labels',
          'Outdoor water',
        ]
        const urban = [
          'Residential',
          'Industrial',
          'Commercial',
          'Military',
          'Military limit',
          'Cemetery',
          'Hospital',
          'Stadium',
        ]
        const buildings = ['Building', 'Castle', 'Station']
        const roads = [
          'Road network',
          'Road labels',
          'Bridge',
          'Tunnel',
          'Tunnel path',
          'Pier',
          'Pedestrian',
          'Pedestrian outline',
          'Steps',
        ]
        const allowed = new Set([
          ...base,
          ...terrain,
          ...vegetation,
          ...water,
          ...urban,
          ...buildings,
          ...roads,
        ])
        const routecampBaseLayers = new Set([
          'background',
          'earth',
          'landcover',
          'buildings',
          'earth_label_islands',
        ])
        const routecampLayerPrefixes = ['landuse_', 'water', 'roads_', 'boundaries', 'places_']
        const styleLayers = map.getStyle()?.layers || []
        styleLayers.forEach((ly: { id: string }) => {
          const isRoutecampContextLayer =
            routecampBaseLayers.has(ly.id) ||
            routecampLayerPrefixes.some((prefix) => ly.id.startsWith(prefix))
          if (!allowed.has(ly.id) && !isRoutecampContextLayer) {
            try {
              map.setLayoutProperty(ly.id, 'visibility', 'none')
            } catch {}
          }
        })
      } catch {}
      resolve()
    }
    timeout = window.setTimeout(() => {
      map.off('error', onError)
      map.off('load', onLoad)
      cleanup()
      reject(new Error('Timed out loading the collection share map'))
    }, 10_000)
    map.once('error', onError)
    map.once('load', onLoad)
  })

  // Add all tracks
  workspaceStore.tours.forEach((tour) => {
    tour.tracks.forEach((track, idx) => {
      const coords = getTrackCoordinates(track)
      if (!coords.length) return
      const sourceId = `share-collection-track-${tour.clientId}-${idx}`
      map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: coords },
        },
      })
      const color = TrackColorService.getTourColor(tour.clientId, track.color)
      map.addLayer({
        id: `${sourceId}-outline`,
        type: 'line',
        source: sourceId,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#ffffff', 'line-width': 8, 'line-opacity': 0.85 },
      })
      map.addLayer({
        id: `${sourceId}-line`,
        type: 'line',
        source: sourceId,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': color, 'line-width': 4 },
      })
    })
  })

  try {
    await new Promise<void>((resolve, reject) => {
      let timeout = 0
      const onIdle = () => {
        clearTimeout(timeout)
        resolve()
      }
      timeout = window.setTimeout(() => {
        map.off('idle', onIdle)
        reject(new Error('Timed out waiting for collection share map tiles'))
      }, 10_000)
      map.once('idle', onIdle)
    })

    return map.getCanvas().toDataURL('image/png')
  } catch (error) {
    logService.warn(LOG_CATEGORIES.MAP, 'Map snapshot failed, using fallback', { error })
    usedFallback.value = true
    return await drawFallbackWorkspace(width, height)
  } finally {
    cleanup()
  }
}

function getWorkspaceBounds(): {
  minLon: number
  minLat: number
  maxLon: number
  maxLat: number
} | null {
  let minLon = Infinity
  let minLat = Infinity
  let maxLon = -Infinity
  let maxLat = -Infinity
  let found = false
  workspaceStore.tours.forEach((tour) => {
    tour.tracks.forEach((track) => {
      const coords = getTrackCoordinates(track)
      coords.forEach(([lon, lat]) => {
        found = true
        if (lon < minLon) minLon = lon
        if (lon > maxLon) maxLon = lon
        if (lat < minLat) minLat = lat
        if (lat > maxLat) maxLat = lat
      })
    })
  })
  if (!found) return null
  return { minLon, minLat, maxLon, maxLat }
}

async function drawFallbackWorkspace(width: number, height: number): Promise<string> {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')!

  // Background
  ctx.fillStyle = '#e9eef3'
  ctx.fillRect(0, 0, width, height)

  // Subtle grid
  ctx.strokeStyle = '#d6dde5'
  ctx.lineWidth = 1
  for (let x = 0; x < width; x += 60) {
    ctx.beginPath()
    ctx.moveTo(x, 0)
    ctx.lineTo(x, height)
    ctx.stroke()
  }
  for (let y = 0; y < height; y += 60) {
    ctx.beginPath()
    ctx.moveTo(0, y)
    ctx.lineTo(width, y)
    ctx.stroke()
  }

  const bounds = getWorkspaceBounds()!
  const padding = 50
  const mercY = (lat: number) => {
    const rad = (lat * Math.PI) / 180
    return Math.log(Math.tan(Math.PI / 4 + rad / 2))
  }
  const minY = mercY(bounds.minLat)
  const maxY = mercY(bounds.maxLat)
  const spanX = bounds.maxLon - bounds.minLon || 1e-6
  const spanY = maxY - minY || 1e-6
  const drawW = width - padding * 2
  const drawH = height - padding * 2
  const scale = Math.min(drawW / spanX, drawH / spanY)
  const project = (lon: number, lat: number) => {
    const x = (lon - bounds.minLon) * scale + padding
    const y = (maxY - mercY(lat)) * scale + padding
    return { x, y }
  }

  workspaceStore.tours.forEach((tour) => {
    tour.tracks.forEach((track) => {
      const coords = getTrackCoordinates(track)
      if (!coords.length) return
      // Outline
      ctx.lineJoin = 'round'
      ctx.lineCap = 'round'
      ctx.strokeStyle = '#ffffff'
      ctx.lineWidth = 8
      ctx.beginPath()
      coords.forEach(([lon, lat], idx) => {
        const { x, y } = project(lon, lat)
        if (idx === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      })
      ctx.stroke()
      // Line
      ctx.strokeStyle = TrackColorService.getTourColor(tour.clientId, track.color)
      ctx.lineWidth = 4
      ctx.beginPath()
      coords.forEach(([lon, lat], idx) => {
        const { x, y } = project(lon, lat)
        if (idx === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      })
      ctx.stroke()
    })
  })

  return canvas.toDataURL('image/png')
}

async function composeWorkspaceImage(
  baseMapDataUrl: string,
  width: number,
  height: number,
): Promise<string> {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')!

  // Draw base map
  const img = await loadImage(baseMapDataUrl)
  ctx.drawImage(img, 0, 0, width, height)

  // Bottom gradient overlay panel
  const panelHeight = Math.round(height * 0.28)
  const grad = ctx.createLinearGradient(0, height - panelHeight, 0, height)
  grad.addColorStop(0, 'rgba(0,0,0,0.0)')
  grad.addColorStop(0.35, 'rgba(0,0,0,0.45)')
  grad.addColorStop(1, 'rgba(0,0,0,0.65)')
  ctx.fillStyle = grad
  ctx.fillRect(0, height - panelHeight, width, panelHeight)

  const pad = 36
  ctx.fillStyle = '#ffffff'
  ctx.textBaseline = 'top'

  // Title (workspace name)
  ctx.font = 'bold 40px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif'
  const title = currentWorkspace.value?.name || 'Collection'
  ctx.fillText(title, pad, height - panelHeight + pad)

  // Date range (start – end) with fallback to waypoint times
  ctx.font = '500 28px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif'
  const normalizeTs = (val: unknown): number | undefined => {
    if (typeof val === 'number' && isFinite(val) && val > 0) return val
    if (typeof val === 'string') {
      if (/^\d+$/.test(val)) {
        const n = Number(val)
        return isFinite(n) && n > 0 ? n : undefined
      }
      const p = Date.parse(val)
      return isNaN(p) ? undefined : p
    }
    return undefined
  }
  type PointLike = { time?: unknown }
  type SegmentLike = { points?: PointLike[] }
  type TrackLike = { startTime?: unknown; endTime?: unknown; segments?: SegmentLike[] }
  type TourLike = { tracks?: TrackLike[]; waypoints?: PointLike[] }

  const getTourTimeRangeApprox = (tour: TourLike): { start?: number; end?: number } => {
    let start: number | undefined
    let end: number | undefined
    // Prefer explicit track start/end times (normalize strings/ISO)
    tour.tracks?.forEach((track: TrackLike) => {
      const s = normalizeTs(track?.startTime)
      const e = normalizeTs(track?.endTime)
      if (typeof s === 'number') start = start === undefined ? s : Math.min(start, s)
      if (typeof e === 'number') end = end === undefined ? e : Math.max(end, e)
    })
    // Fallback: derive from waypoints on tracks/segments and tour-level waypoints
    if (!start || !end) {
      // Track segment points
      tour.tracks?.forEach((track: TrackLike) => {
        track.segments?.forEach((seg: SegmentLike) => {
          seg.points?.forEach((p: PointLike) => {
            if (typeof p?.time === 'number') {
              start = start === undefined ? p.time : Math.min(start, p.time)
              end = end === undefined ? p.time : Math.max(end, p.time)
            }
          })
        })
      })
      // Tour-level waypoints
      tour.waypoints?.forEach((p: PointLike) => {
        if (typeof p?.time === 'number') {
          start = start === undefined ? p.time : Math.min(start, p.time)
          end = end === undefined ? p.time : Math.max(end, p.time)
        }
      })
    }
    return { start, end }
  }

  let earliest: number | undefined
  let latest: number | undefined
  let toursWithTime = 0
  workspaceStore.tours.forEach((t) => {
    const { start, end } = getTourTimeRangeApprox(t)
    if (typeof start === 'number') {
      earliest = earliest === undefined ? start : Math.min(earliest, start)
    }
    if (typeof end === 'number') {
      latest = latest === undefined ? end : Math.max(latest, end)
    }
    if (typeof start === 'number' || typeof end === 'number') toursWithTime++
  })
  const userLocale =
    (navigator as Navigator & { languages?: readonly string[] }).languages?.[0] ??
    navigator.language
  const fmtDate = (ts: number) =>
    new Date(ts).toLocaleDateString(userLocale || undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  const dateLine =
    typeof earliest === 'number' && typeof latest === 'number'
      ? earliest === latest
        ? fmtDate(earliest)
        : `${fmtDate(earliest)} – ${fmtDate(latest)}`
      : typeof earliest === 'number'
        ? fmtDate(earliest)
        : typeof latest === 'number'
          ? fmtDate(latest)
          : ''
  logService.info(LOG_CATEGORIES.UI, 'Workspace share image dates computed', {
    tours: workspaceStore.tours.length,
    toursWithTime,
    earliest,
    latest,
    dateLine,
  })
  if (dateLine) {
    ctx.fillText(dateLine, pad, height - panelHeight + pad + 56)
  }

  // Stats line
  const stats = [
    `${distanceKm.value} km`,
    elevationHm.value ? `${elevationHm.value} hm` : '',
    durationText.value ? `${durationText.value}` : '',
  ].filter(Boolean)
  ctx.font = '600 32px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif'
  ctx.fillText(stats.join('   •   '), pad, height - panelHeight + pad + 56 + 40)

  return canvas.toDataURL('image/png')
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = (e) => reject(e)
    img.src = src
  })
}

async function shareCollectionImage() {
  if (!imageDataUrl.value) return
  const shareUrl = isPublic.value ? workspaceUrl.value : ''
  const stats = [
    `${distanceKm.value} km`,
    elevationHm.value ? `${elevationHm.value} hm` : '',
    durationText.value ? durationText.value : '',
  ].filter(Boolean)
  const parts = [
    currentWorkspace.value?.name || 'Collection',
    `\n${stats.join(', ')}`,
    shareUrl ? `\n${shareUrl}` : '',
  ]
  const shareText = parts.filter(Boolean).join('')

  try {
    const blob = await (await fetch(imageDataUrl.value)).blob()
    const file = new File([blob], `${currentWorkspace.value?.name || 'collection'}.png`, {
      type: 'image/png',
    })
    const canShareFiles = (
      navigator as Navigator & { canShare?: (data: unknown) => boolean }
    ).canShare?.({ files: [file] })
    if (navigator.share && canShareFiles) {
      await navigator.share({ files: [file], text: shareText })
      return
    }
    if (navigator.share) {
      await navigator.share({ text: shareText, url: shareUrl || undefined })
      return
    }
  } catch (e) {
    logService.warn(LOG_CATEGORIES.UI, 'Web Share with file failed', { e })
  }

  const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`
  window.open(url, '_blank')
}

async function downloadCollectionImage() {
  if (!imageDataUrl.value) return
  try {
    const blob = await (await fetch(imageDataUrl.value)).blob()
    await downloadFile(blob, `${currentWorkspace.value?.name || 'collection'}.png`, {
      mimeType: 'image/png',
    })
  } catch (error) {
    logService.error(LOG_CATEGORIES.FILE, 'Failed to download collection share image', { error })
    showMessage.error('Failed to download image')
  }
}

watch(
  () => workspaceStore.currentWorkspace,
  (newWorkspace) => {
    currentWorkspace.value = newWorkspace
    if (newWorkspace) {
      isPublic.value = newWorkspace.isPublic || false
    }
  },
  { immediate: true },
)

// Generate once per modal opening. Switching tabs keeps the existing preview.
watch(
  () => props.isOpen,
  async (open) => {
    if (open) {
      await generateShareImage()
    }
  },
)

// Regenerate preview when tours/tracks count changes while share tab is open
watch(
  () => [
    workspaceStore.tours.length,
    workspaceStore.tours.reduce((sum, t) => sum + t.tracks.length, 0),
  ],
  async () => {
    if (props.isOpen && activeTab.value === 'share' && !isGenerating.value) {
      await generateShareImage()
    }
  },
)

watch(
  () => props.isOpen,
  async (isOpen) => {
    if (isOpen) {
      if (canManageSharing.value) {
        await Promise.all([loadMembers(), loadInvitations()])
      }
    } else {
      messages.value = []
      activeTab.value = 'share'
      memberSearchQuery.value = ''
      invitationSearchQuery.value = ''
    }
  },
)

watch(
  () => activeTab.value,
  async (newTab) => {
    if (newTab === 'people') {
      await loadInvitedUsers()
    }
  },
)
</script>

<template>
  <Teleport to="body">
    <BaseModal
      :is-open="isOpen"
      title="Share collection"
      :description="currentWorkspace?.name || 'Manage collection sharing and access'"
      size="2xl"
      body-class="p-0"
      header-class="!px-4 !py-3 sm:!p-4"
      content-class="!mx-2 max-h-[85dvh] !rounded-2xl sm:!mx-4 sm:max-h-[85vh] sm:!rounded-lg"
      overlay-class="px-2 pt-[max(env(safe-area-inset-top),8px)] pb-[max(env(safe-area-inset-bottom),8px)] sm:p-4"
      @close="emit('close')"
    >
      <template #header-extra>
        <p v-if="currentWorkspace?.name" class="mt-0.5 truncate text-sm font-normal text-gray-500">
          {{ currentWorkspace.name }}
        </p>
      </template>

      <div
        v-if="canManageSharing"
        class="sticky top-0 z-10 border-b border-gray-100 bg-white px-3 py-2 sm:px-5 sm:py-3"
      >
        <BaseSegmentedControl
          :model-value="activeTab"
          :options="tabOptions"
          aria-label="Collection sharing sections"
          option-id-prefix="collection-share-tab"
          panel-id-prefix="collection-share-panel"
          @update:model-value="setActiveTab"
        />
      </div>

      <!-- Messages -->
      <div v-if="messages.length > 0" class="space-y-2 px-3 pt-3 sm:px-5">
        <div
          v-for="message in messages"
          :key="message.id"
          :class="[
            // Base styles
            'flex',
            'items-center',
            'justify-between',
            'p-3',
            'rounded-md',
            'border',
            'text-sm',
            // Type-specific styles
            {
              'bg-green-50 border-green-200 text-green-800': message.type === 'success',
              'bg-red-50 border-red-200 text-red-800': message.type === 'error',
              'bg-blue-50 border-blue-200 text-blue-800': message.type === 'info',
            },
          ]"
        >
          <div class="flex items-center space-x-2">
            <CheckCircleIcon v-if="message.type === 'success'" class="h-4 w-4 flex-shrink-0" />
            <AlertTriangleIcon v-if="message.type === 'error'" class="h-4 w-4 flex-shrink-0" />
            <InfoIcon v-if="message.type === 'info'" class="h-4 w-4 flex-shrink-0" />
            <span>{{ message.text }}</span>
          </div>
          <button
            @click="removeMessage(message.id)"
            :class="[
              'ml-2',
              'hover:cursor-pointer',
              'rounded-full',
              'p-1',
              'hover:bg-black/10',
              'hover:scale-110',
              'hover:rotate-90',
              'transition-all',
              'duration-150',
              'ease-out',
              'active:scale-95',
              'focus:outline-none',
              'focus:ring-1',
              'focus:ring-primary-500',
              {
                'text-green-600 hover:text-green-800': message.type === 'success',
                'text-red-600 hover:text-red-800': message.type === 'error',
                'text-blue-600 hover:text-blue-800': message.type === 'info',
              },
            ]"
            aria-label="Dismiss message"
          >
            <XIcon class="h-3 w-3 transition-transform duration-150 ease-out" />
          </button>
        </div>
      </div>

      <!-- Tab Content -->
      <div>
        <!-- Share Tab -->
        <div
          v-if="activeTab === 'share' || !canManageSharing"
          id="collection-share-panel-share"
          role="tabpanel"
          aria-labelledby="collection-share-tab-share"
          class="space-y-4 p-3 sm:p-5"
        >
          <div v-if="!canShare" class="rounded-xl border border-yellow-200 bg-yellow-50 p-3">
            <p class="text-sm text-yellow-800">{{ shareBlockedReason }}</p>
          </div>

          <div class="grid items-start gap-4 md:grid-cols-[minmax(0,1fr)_260px] md:gap-5">
            <div class="contents md:block md:min-w-0 md:space-y-4">
              <button
                type="button"
                role="switch"
                :aria-checked="isPublic"
                :disabled="!canShare || !canManageSharing"
                :title="
                  canManageSharing ? undefined : 'Only the collection owner can change visibility'
                "
                class="order-1 flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-60 sm:p-4 md:order-none"
                :class="
                  canShare && canManageSharing
                    ? 'hover:cursor-pointer hover:border-gray-300 hover:bg-gray-100'
                    : ''
                "
                @click="handleToggle"
              >
                <span
                  class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-gray-200 sm:h-10 sm:w-10"
                >
                  <GlobeIcon v-if="isPublic" class="h-5 w-5 text-primary-600" />
                  <LockIcon v-else class="h-5 w-5 text-gray-500" />
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block text-sm font-semibold text-gray-900">
                    {{ isPublic ? 'Public link enabled' : 'Private collection' }}
                  </span>
                  <span class="mt-0.5 block text-xs leading-4 text-gray-500 sm:leading-5">
                    {{
                      isPublic
                        ? 'Anyone with the link can view this collection.'
                        : 'Only invited people can open this collection.'
                    }}
                  </span>
                </span>
                <BaseToggle :model-value="isPublic" />
              </button>

              <div
                v-if="isPublic && canShare"
                class="order-2 rounded-xl border border-primary-200 bg-primary-50 p-3 sm:p-4 md:order-none"
              >
                <div class="mb-2 flex items-center gap-2 text-sm font-semibold text-primary-900">
                  <LinkIcon class="h-4 w-4" />
                  Public link
                </div>
                <div class="flex min-w-0 items-center gap-2">
                  <input
                    :value="workspaceUrl"
                    readonly
                    aria-label="Public collection link"
                    class="min-w-0 flex-1 rounded-xl border border-primary-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm"
                  />
                  <BaseButton variant="secondary" size="sm" class="flex-none" @click="copyUrl">
                    <CopyIcon class="mr-1.5 h-4 w-4" />
                    Copy
                  </BaseButton>
                </div>
              </div>

              <div class="order-4 md:order-none md:rounded-xl md:border md:border-gray-200 md:p-4">
                <h3 class="hidden text-sm font-semibold text-gray-900 md:block">Send or save</h3>
                <p class="mt-1 hidden text-xs leading-5 text-gray-500 md:block">
                  Share the generated map card or save it as an image.
                </p>
                <div class="grid grid-cols-2 gap-2 md:mt-3 md:grid-cols-1">
                  <BaseButton
                    variant="primary"
                    full-width
                    class="min-h-11 md:min-h-0"
                    :disabled="!imageDataUrl || isGenerating"
                    @click="shareCollectionImage"
                  >
                    <Share2Icon class="mr-2 h-4 w-4" />
                    <span class="md:hidden">Share</span>
                    <span class="hidden md:inline">Share image</span>
                  </BaseButton>
                  <BaseButton
                    variant="secondary"
                    full-width
                    class="min-h-11 md:min-h-0"
                    :disabled="!imageDataUrl || isGenerating"
                    @click="downloadCollectionImage"
                  >
                    <DownloadIcon class="mr-2 h-4 w-4" />
                    <span class="md:hidden">Save</span>
                    <span class="hidden md:inline">Download image</span>
                  </BaseButton>
                </div>
              </div>

              <div
                v-if="isPublic"
                class="order-5 rounded-xl bg-gray-50 p-3 text-xs leading-5 text-gray-600 md:order-none"
              >
                Tours, photos, and other content are visible to anyone with the link and may appear
                in public gallery and map views.
              </div>
            </div>

            <div class="order-3 mx-auto w-full md:order-none">
              <div class="mb-2 flex items-center justify-between">
                <div>
                  <h3 class="text-sm font-semibold text-gray-900">Share image</h3>
                  <p class="text-xs text-gray-500">Map, title, and collection stats</p>
                </div>
                <BaseIconButton
                  size="md"
                  :disabled="isGenerating"
                  title="Regenerate image"
                  aria-label="Regenerate image"
                  @click="generateShareImage"
                >
                  <RefreshCwIcon class="h-4 w-4" :class="isGenerating ? 'animate-spin' : ''" />
                </BaseIconButton>
              </div>
              <div
                class="relative aspect-square overflow-hidden rounded-xl border border-gray-200 bg-gray-100 shadow-sm"
              >
                <img
                  v-if="imageDataUrl"
                  :src="imageDataUrl"
                  alt="Workspace share preview"
                  class="h-full w-full object-cover"
                />
                <div
                  v-if="isGenerating"
                  class="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/80 text-sm text-gray-600 backdrop-blur-sm"
                >
                  <BaseSpinner size="md" />
                  Generating preview…
                </div>
                <div
                  v-else-if="errorText"
                  class="absolute inset-0 flex flex-col items-center justify-center gap-3 p-5 text-center"
                >
                  <AlertTriangleIcon class="h-6 w-6 text-danger-500" />
                  <p class="text-sm text-danger-700">{{ errorText }}</p>
                  <BaseButton
                    size="sm"
                    variant="secondary"
                    class="flex-none"
                    @click="generateShareImage"
                  >
                    Try again
                  </BaseButton>
                </div>
                <div
                  v-else-if="!imageDataUrl"
                  class="absolute inset-0 flex items-center justify-center text-sm text-gray-500"
                >
                  No preview available
                </div>
              </div>
              <p
                v-if="usedFallback"
                class="mt-2 rounded-lg border border-yellow-200 bg-yellow-50 p-2 text-xs leading-5 text-yellow-800"
              >
                Using a simplified map preview because the full basemap could not be exported.
              </p>
            </div>
          </div>
        </div>

        <!-- People Tab -->
        <div
          v-else
          id="collection-share-panel-people"
          role="tabpanel"
          aria-labelledby="collection-share-tab-people"
          class="space-y-6 p-3 sm:p-5"
        >
          <section class="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div class="mb-3 flex items-center gap-2">
              <UserPlusIcon class="h-4 w-4 text-primary-600" />
              <div>
                <h3 class="text-sm font-semibold text-gray-900">Invite someone</h3>
              </div>
            </div>

            <div class="grid gap-3 sm:grid-cols-[minmax(0,1fr)_150px_auto] sm:items-end">
              <div class="relative min-w-0">
                <label
                  for="share-invite-email"
                  class="mb-1 block text-xs font-medium text-gray-600"
                >
                  Email address
                </label>
                <BaseInput
                  id="share-invite-email"
                  v-model="memberEmailValue"
                  type="email"
                  placeholder="name@example.com"
                  autocomplete="off"
                  inputmode="email"
                  autocapitalize="off"
                  autocorrect="off"
                  spellcheck="false"
                  data-lpignore="true"
                  data-1p-ignore
                  :readonly="emailReadonly"
                  role="combobox"
                  aria-autocomplete="list"
                  :aria-expanded="showEmailSuggestions ? 'true' : 'false'"
                  :aria-controls="suggestionListId"
                  :aria-invalid="hasTriedInvite && memberEmailError ? 'true' : 'false'"
                  :aria-describedby="
                    hasTriedInvite && memberEmailError ? 'member-email-error' : undefined
                  "
                  :invalid="Boolean(hasTriedInvite && memberEmailError)"
                  @focus="onEmailFocus"
                  @blur="onEmailBlur"
                  @keydown="onEmailKeydown"
                />
                <div
                  v-if="showEmailSuggestions"
                  :id="suggestionListId"
                  class="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
                  role="listbox"
                  aria-label="Email suggestions"
                >
                  <button
                    v-for="(suggestion, index) in emailSuggestions"
                    :id="`${suggestionListId}-opt-${index}`"
                    :key="suggestion.email"
                    type="button"
                    class="w-full px-3 py-2 text-left focus:outline-none"
                    :class="highlightedIndex === index ? 'bg-gray-100' : 'hover:bg-gray-50'"
                    @mousedown.prevent
                    @mouseenter="highlightedIndex = index"
                    @click="selectEmailSuggestion(suggestion)"
                  >
                    <div
                      class="text-sm text-gray-900"
                      v-html="highlightAutocompleteMatch(suggestion.email)"
                    ></div>
                    <div
                      v-if="suggestion.profileName"
                      class="text-xs text-gray-500"
                      v-html="highlightAutocompleteMatch(suggestion.profileName)"
                    ></div>
                  </button>
                </div>
                <p
                  v-if="hasTriedInvite && memberEmailError"
                  id="member-email-error"
                  class="mt-1 text-xs text-danger-600"
                  role="alert"
                >
                  {{ memberEmailError }}
                </p>
              </div>

              <div>
                <label class="mb-1 block text-xs font-medium text-gray-600">Role</label>
                <BaseSelect
                  :model-value="newMemberRole"
                  :options="roleOptions"
                  aria-label="Invitation role"
                  @update:model-value="newMemberRole = Number($event) as WorkspaceRoleType"
                />
              </div>

              <BaseButton
                class="sm:flex-none"
                :disabled="!memberEmailValue.trim()"
                :loading="isInviting"
                full-width
                @click="inviteMember"
              >
                Invite
              </BaseButton>
            </div>
          </section>

          <section class="space-y-3">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 class="text-sm font-semibold text-gray-900">Members</h3>
                <p class="text-xs text-gray-500">{{ members.length }} with collection access</p>
              </div>
              <BaseSearchBar
                v-if="members.length > 3"
                v-model="memberSearchQuery"
                class="w-full sm:max-w-56"
                placeholder="Search members"
                aria-label="Search members"
                @focus="getInputHandlers().onFocus"
                @blur="getInputHandlers().onBlur"
              />
            </div>

            <div v-if="isLoadingMembers" class="flex justify-center py-6">
              <BaseSpinner size="md" />
            </div>
            <BaseEmptyState
              v-else-if="members.length === 0"
              :icon="UsersIcon"
              size="compact"
              title="No members yet"
              description="Invite someone above to give them access."
            />
            <BaseEmptyState
              v-else-if="filteredMembers.length === 0"
              :icon="UsersIcon"
              size="compact"
              title="No matching members"
            />
            <div
              v-else
              class="divide-y divide-gray-100 overflow-visible rounded-xl border border-gray-200 bg-white"
            >
              <div
                v-for="member in filteredMembers"
                :key="member.userId"
                class="flex flex-col gap-3 p-3 transition-colors hover:bg-gray-50 sm:flex-row sm:items-center"
              >
                <div class="flex min-w-0 flex-1 items-center gap-3">
                  <div
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700"
                  >
                    {{ member.name.charAt(0).toUpperCase() }}
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-sm font-medium text-gray-900">{{ member.name }}</p>
                    <p class="truncate text-xs text-gray-500">{{ member.email }}</p>
                  </div>
                </div>
                <div class="flex items-center gap-2 sm:justify-end">
                  <BaseSelect
                    v-if="!isOwnerMember(member)"
                    class="min-w-32 flex-1 sm:flex-none"
                    :model-value="member.role"
                    :options="roleOptions"
                    :aria-label="`Role for ${member.name}`"
                    @update:model-value="
                      updateMemberRole(member, Number($event) as WorkspaceRoleType)
                    "
                  />
                  <BaseBadge v-else tone="neutral">Owner</BaseBadge>
                  <BaseIconButton
                    v-if="!isOwnerMember(member)"
                    class="h-11 w-11 sm:h-9 sm:w-9"
                    size="lg"
                    tone="danger"
                    :title="`Remove ${member.name}`"
                    :aria-label="`Remove ${member.name}`"
                    @click="removeMember(member)"
                  >
                    <TrashIcon class="h-4 w-4" />
                  </BaseIconButton>
                </div>
              </div>
            </div>
          </section>

          <section class="space-y-3">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div class="flex items-center gap-2">
                  <h3 class="text-sm font-semibold text-gray-900">Pending invitations</h3>
                  <BaseBadge v-if="invitations.length" tone="warning">{{
                    invitations.length
                  }}</BaseBadge>
                </div>
                <p class="text-xs text-gray-500">Invitations that have not been accepted yet</p>
              </div>
              <BaseSearchBar
                v-if="invitations.length > 3"
                v-model="invitationSearchQuery"
                class="w-full sm:max-w-56"
                placeholder="Search invitations"
                aria-label="Search invitations"
                @focus="getInputHandlers().onFocus"
                @blur="getInputHandlers().onBlur"
              />
            </div>

            <div v-if="isLoadingInvitations" class="flex justify-center py-6">
              <BaseSpinner size="md" />
            </div>
            <BaseEmptyState
              v-else-if="invitations.length === 0"
              :icon="MailIcon"
              size="compact"
              title="No pending invitations"
            />
            <BaseEmptyState
              v-else-if="filteredInvitations.length === 0"
              :icon="MailIcon"
              size="compact"
              title="No matching invitations"
            />
            <div
              v-else-if="filteredInvitations.length"
              class="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white"
            >
              <div
                v-for="invitation in filteredInvitations"
                :key="invitation.id"
                class="flex items-start gap-3 p-3 transition-colors hover:bg-gray-50"
              >
                <div
                  class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-yellow-100"
                >
                  <MailIcon class="h-5 w-5 text-yellow-700" />
                </div>
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm font-medium text-gray-900">{{ invitation.email }}</p>
                  <p class="mt-0.5 text-xs leading-5 text-gray-500">
                    {{ invitation.roleDisplay }} · invited
                    {{ new Date(invitation.createdAt).toLocaleDateString() }}
                  </p>
                </div>
                <div class="flex shrink-0 items-center gap-1">
                  <BaseBadge :tone="invitation.isExpired ? 'danger' : 'warning'">
                    {{ invitation.isExpired ? 'Expired' : 'Pending' }}
                  </BaseBadge>
                  <BaseIconButton
                    class="h-11 w-11 sm:h-9 sm:w-9"
                    size="lg"
                    tone="danger"
                    title="Cancel invitation"
                    :aria-label="`Cancel invitation for ${invitation.email}`"
                    @click="cancelInvitation(invitation)"
                  >
                    <TrashIcon class="h-4 w-4" />
                  </BaseIconButton>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </BaseModal>
  </Teleport>
</template>
